import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../hooks/useTheme";
import AppSidebar from "./AppSidebar";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import {
  FormInput,
  FormTextarea,
  FormButton,
  DaySelector,
} from "./FormComponents";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { createHabit } from "../store/slices/habitsSlice";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import { DayOfWeek } from "../types";
import { useToastContext } from "./ToastContext";

const AddHabit: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { loading, error } = useAppSelector((state) => state.habits);
  const { addToast } = useToastContext();
  const location = useLocation();
  const userProfile = useAppSelector((state) => state.user.profile);
  const { isDark, setTheme } = useTheme();
  const initials = userProfile?.name
    ? userProfile.name
        .split(" ")
        .map((w: string) => w.charAt(0).toUpperCase())
        .slice(0, 2)
        .join("")
    : (userProfile?.username?.charAt(0).toUpperCase() ?? "U");

  const handleThemeToggle = () => {
    setTheme(isDark ? "light" : "dark");
  };

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const mobileNavLinks = [
    { path: "/app", emoji: "📅", label: t("nav.calendar") },
    { path: "/habits", emoji: "🔥", label: t("nav.habits") },
    { path: "/todos", emoji: "✅", label: t("nav.todos") },
    { path: "/trackers", emoji: "📊", label: t("nav.trackers") },
    { path: "/notes", emoji: "📝", label: t("nav.notes") },
  ];

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    activeForDays: [] as DayOfWeek[],
  });

  useEffect(() => {
    trackPageVisit("add_habit");
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      return;
    }

    try {
      // Dispatch createHabit action to Redux store
      const result = await dispatch(
        createHabit({
          name: formData.name.trim(),
          isActive: true,
          isPaused: false,
          activeForDays:
            formData.activeForDays.length > 0
              ? formData.activeForDays
              : undefined,
        }),
      ).unwrap();

      // Show toast if points earned
      if (result.pointsEarned > 0) {
        addToast(
          t("toast.pointsEarned", { points: result.pointsEarned }),
          "success",
        );
      }

      // Track habit creation event
      trackUserInteraction(
        "create_habit",
        "habit_management",
        result?.id || "unknown",
      );

      // Navigate to habits page
      navigate("/habits");
    } catch (error) {
      console.error("Error saving habit:", error);
      // Error is handled by Redux state
    }
  };

  const handleCancel = () => {
    navigate("/habits");
  };

  const handleDayToggle = (day: DayOfWeek) => {
    setFormData((prev) => ({
      ...prev,
      activeForDays: prev.activeForDays.includes(day)
        ? prev.activeForDays.filter((d) => d !== day)
        : [...prev.activeForDays, day],
    }));
  };

  return (
    <>
      <MetaHead
        title={t("addHabit.title")}
        description={t("addHabit.description") as string}
        keywords={t("addHabit.keywords") as string}
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#3b5cde]/20 border border-[#3b5cde]/30 flex items-center justify-center transition-all hover:bg-[#3b5cde]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("addHabit.headerTitle")}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-gray-500 dark:text-white/50 text-[19px] flex items-center justify-center cursor-pointer transition-all hover:bg-black/10 dark:hover:bg-white/10"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                aria-label="Go to profile"
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#3b5cde]/15 border border-[#3b5cde]/25 flex items-center justify-center transition-all hover:bg-[#3b5cde]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {t("addHabit.headerTitle")}
            </h1>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            <div className="mx-auto">
              {/* Intro Card */}
              <div className="mb-6 glass-card p-5 rounded-xl border border-[#f5a623]/20 dark:border-[#f5a623]/30 bg-gradient-to-br from-[#f5a623]/[0.05] to-transparent dark:from-[#f5a623]/[0.08]">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#f5a623]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[24px] leading-none">🔥</span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-[19px] font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
                      {t("addHabit.createNewHabit")}
                    </h2>
                    <p className="text-[17px] text-neutral-600 dark:text-neutral-400 leading-relaxed">
                      {t("addHabit.habitIntroDesc")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Banner */}
              {error && (
                <div className="mb-5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-red-500 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-red-700 dark:text-red-300 text-sm font-medium">
                      {error}
                    </p>
                  </div>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Habit Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-[17px] font-medium text-neutral-900 dark:text-neutral-100 mb-2.5"
                  >
                    {t("addHabit.habitNameLabel")}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <FormInput
                    id="name"
                    label=""
                    value={formData.name}
                    onChange={(value) =>
                      setFormData({ ...formData, name: value })
                    }
                    placeholder={t("addHabit.habitNamePlaceholder") as string}
                  />
                  <p className="text-[15px] text-neutral-500 dark:text-neutral-400 mt-1.5">
                    {formData.name.length}/100 {t("addHabit.characters")}
                  </p>
                </div>

                {/* Description */}
                <FormTextarea
                  id="description"
                  label={t("addHabit.descriptionLabel") as string}
                  value={formData.description}
                  onChange={(value) =>
                    setFormData({ ...formData, description: value })
                  }
                  placeholder={t("addHabit.descriptionPlaceholder") as string}
                  rows={3}
                />

                {/* Active Days */}
                <DaySelector
                  selectedDays={formData.activeForDays}
                  onDayToggle={handleDayToggle}
                  label={t("addHabit.activeDaysLabel") as string}
                />

                {/* Schedule Preview */}
                <div className="p-3.5 bg-[#f5a623]/[0.06] dark:bg-[#f5a623]/[0.08] border border-[#f5a623]/[0.15] dark:border-[#f5a623]/[0.2] rounded-lg">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#f5a623] mt-1.5 flex-shrink-0" />
                    <p className="text-[16px] text-neutral-700 dark:text-neutral-300 leading-relaxed">
                      {formData.activeForDays.length > 0
                        ? t("addHabit.schedulingTextWithDays", {
                            days: formData.activeForDays
                              .map(
                                (day) =>
                                  day.charAt(0).toUpperCase() + day.slice(1),
                              )
                              .join(", "),
                          })
                        : t("addHabit.schedulingTextDefault")}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <div className="">
                    <FormButton
                      type="submit"
                      variant="primary"
                      loading={loading}
                      disabled={!formData.name.trim() || loading}
                      size="lg"
                    >
                      {loading
                        ? (t("addHabit.creating") as string)
                        : (t("addHabit.createHabit") as string)}
                    </FormButton>
                  </div>
                  <div className="">
                    <FormButton
                      type="button"
                      variant="secondary"
                      onClick={handleCancel}
                      disabled={loading}
                      size="lg"
                    >
                      {t("addHabit.cancel") as string}
                    </FormButton>
                  </div>
                </div>
              </form>

              {/* Help Section */}
              <div className="mt-8 p-5 glass-card rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#f5a623]/[0.15] flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-[#f5a623]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[17px] font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
                      {t("addHabit.helpTitle")}
                    </h3>
                    <p className="text-[16px] text-neutral-600 dark:text-neutral-400 leading-relaxed">
                      {t("addHabit.helpBody")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* MOBILE BOTTOM NAV */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 backdrop-blur-sm border-t border-black/[0.08] dark:border-white/[0.08]">
          <div className="flex items-center justify-around px-2 pt-2 pb-2">
            {mobileNavLinks.map(({ path, emoji, label }) => {
              const active = isNavActive(path);
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="flex flex-col items-center gap-1 cursor-pointer"
                  style={{ color: active ? "#f5a623" : undefined }}
                >
                  <span className="text-[22px]">{emoji}</span>
                  <span
                    className={`text-[13px] font-medium${active ? "" : " text-gray-400 dark:text-white/25"}`}
                    style={active ? { color: "#f5a623" } : undefined}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
};

export default AddHabit;
